import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import LoadingSkeleton from '~Components/LinkUser/SelectedUser/LoadingSkeleton';
import { useStorage } from '~Components/Storage';
import DateInput from '~ComponentsImproved/DateInput';
import Loader from '~ComponentsImproved/Loader';
import Toggle from '~ComponentsImproved/Toggle';
import { useButton, useI18n, useSelector } from '~Hooks';
import { formatSearchSum } from '~Scenes/Handy/Scenes/History/utils';
import { OutlinedButton, Box, BaseDarkInputStyling } from '~Styles';
import { SEARCH_ORDERS_QUERY } from './queries';
import {
  getTables,
  getTablesVariables,
  SearchOrders,
  SearchOrders_searchOrders_results,
  SearchOrdersVariables,
} from './__queries__';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import OrderRow from './OrderRow';
import { useTableUniverse } from '~Components/TableUniverse';
import { Cheque } from '@baemingo/table-events/dist/types';
import ChequeRow from './ChequeRow';
import { handyBreakpoint } from '~Styles/breakpoints';

const GET_TABLES = gql`
  query getTables($id: ID!) {
    venue: Venue(id: $id) {
      id
      tables {
        id
        code
      }
    }
  }
`;

const Input = styled.input<{ dark?: boolean }>`
  margin-bottom: 8px;
  padding: 0 16px;
  font-size: 14px;

  ${BaseDarkInputStyling}
`;

const ShowMoreButton = styled(OutlinedButton)`
  width: 100%;
  margin-top: 16px;
`;

const OrdersListContainer = styled.div`
  padding: 0 8px 40px;
  overflow-y: auto;
  height: 100%;

  ${handyBreakpoint} {
    padding: 0 0 40px;
  }
`;

export interface ChequeWithTableCode extends Cheque {
  tableCode?: string;
}

interface Props {
  onOrderClick: (orderId: string) => void;
  onChequeClick: (chequeId: string) => void;
}

const HistoryOrdersList: FC<Props> = ({ onOrderClick, onChequeClick }) => {
  const { i18n } = useI18n();

  const location = useLocation();
  const history = useHistory();

  const locale = useSelector((state) => state.locale);

  // Locale mapping to match moment's format (same as in Translations/index.ts)
  const LOCALE_MAP: Record<string, string> = {
    se: 'sv-SE',
    en: 'en-GB',
    no: 'nb-NO',
    es: 'es-ES',
    dk: 'da-DK',
    tr: 'tr-TR',
  };

  // Format date as "Today", "Yesterday", "Last Wednesday", or readable date (similar to moment's calendar())
  const formatDateLabel = (dateString: string): string => {
    const luxonLocale = LOCALE_MAP[locale] || 'en-GB';
    const orderDate = DateTime.fromISO(dateString)
      .startOf('day')
      .setLocale(luxonLocale);
    const today = DateTime.now().startOf('day');
    const diffDays = Math.floor(today.diff(orderDate, 'days').days);

    // Same day
    if (diffDays === 0) {
      return i18n('General.Today');
    }
    // Yesterday
    if (diffDays === 1) {
      return i18n('General.Yesterday');
    }
    // Last 6 days (2-6 days ago) - show "Last Monday", "Last Tuesday", etc.
    if (diffDays >= 2 && diffDays <= 6) {
      const dayName = orderDate.toLocaleString({ weekday: 'long' });
      return `${i18n('General.Last')} ${dayName}`;
    }
    // Everything else - show date in DD/MM/YYYY format
    return orderDate.toFormat('dd/MM/yyyy');
  };

  const companyId = useSelector((state) => state.company);
  const venueId = useSelector((state) => state.venueId);

  const [storage, updateStorage] = useStorage();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedDate = searchParams.get('date') ?? null;

  const formattedSearchSum = formatSearchSum(searchParams.get('searchsum') ?? null);

  // In progress orders
  const universe = useTableUniverse();

  const getTablesResult = useQuery<getTables, getTablesVariables>(GET_TABLES, {
    variables: { id: venueId || '' },
    skip: !venueId,
  });
  const tables = getTablesResult.data?.venue.tables;

  const availableCheques: ChequeWithTableCode[] = useMemo(() => {
    // Search variables derived from URL params
    const searchVariables = {
      easyId: searchParams.get('searchterm') ?? undefined,
      sum: formattedSearchSum,
      startDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .startOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : undefined,
      endDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .endOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : undefined,
    };

    let cheques: ChequeWithTableCode[] = [...universe.cheques]
      .filter((c) => universe.payments?.some((p) => p.chequeId === c.id))
      .sort(
        (a, b) =>
          DateTime.fromISO(b.createdAt!).toMillis() - DateTime.fromISO(a.createdAt!).toMillis(),
      );

    if (tables) {
      cheques = cheques.map((c) => {
        // Make object extensible
        c = { ...c };
        c.tableCode = tables.find((t) => t.id === c.tableId)?.code;
        return c;
      });

      if (searchVariables.easyId) {
        cheques = cheques.filter((c) => c.tableCode === searchVariables.easyId);
      }
    }

    if (searchVariables.sum) {
      cheques = cheques.filter((c) => c.paidAmount === searchVariables.sum);
    }

    return cheques;
  }, [
    searchParams,
    formattedSearchSum,
    selectedDate,
    tables,
    universe.cheques,
    universe.payments,
  ]);

  const updateSearchParam = (key: 'searchterm' | 'date' | 'searchsum', value: string) => {
    const params = new URLSearchParams(location.search);
    params.set(key, value);
    return params.toString();
  };

  const [orders, setOrders] = useState<SearchOrders_searchOrders_results[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  /** If there is no orders to fetch, don't show the button anymore */
  const [hideLoadMore, setHideLoadMore] = useState<boolean>(false);
  const ORDERS_PER_FETCH = 50; // Fetch first 50 then fetch 50 more each time we press "Show more"

  const loadMoreButton = useButton({
    onClick: () => {
      setPageIndex((prev) => prev + 1);
    },
  });

  const ordersQuery = useQuery<SearchOrders, SearchOrdersVariables>(SEARCH_ORDERS_QUERY, {
    skip: !venueId,
    fetchPolicy: 'no-cache',
    variables: {
      companyId,
      venueIds: [venueId!],
      easyId: searchParams.get('searchterm') ?? undefined,
      sum: formattedSearchSum,
      deviceId: storage.filterOrderHistoryByDevice
        ? storage.taxComplianceDeviceId ?? undefined
        : undefined,
      first: ORDERS_PER_FETCH,
      skip: pageIndex * ORDERS_PER_FETCH,
      startDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .startOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : DateTime.now()
            .minus({ days: 7 })
            .startOf('day')
            .toISO() ?? undefined,
      endDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .endOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : DateTime.now()
            .endOf('day')
            .toISO() ?? undefined,
    },
  });

  useEffect(() => {
    // Only update orders when the query is not loading (i.e., data is fresh)
    if (ordersQuery.loading) return;

    const newlyFetchedOrders = ordersQuery.data?.searchOrders.results ?? [];

    // If pageIndex is 0, we're starting fresh (either first load or filter changed)
    if (pageIndex === 0) {
      setOrders(newlyFetchedOrders);
    } else {
      // Append to existing orders for pagination
      setOrders((prev) => [...prev, ...newlyFetchedOrders]);
    }

    /** If there is less than 50 we know that we have reached the end */
    if (newlyFetchedOrders.length < 50) {
      setHideLoadMore(true);
    }
  }, [pageIndex, ordersQuery.data, ordersQuery.loading]);

  /** Reset pagination and orders when filter changes */
  useEffect(() => {
    setOrders([]);
    setPageIndex(0);
    setHideLoadMore(false);
  }, [
    storage.filterOrderHistoryByDevice,
    searchParams.get('searchterm'),
    searchParams.get('searchsum'),
    selectedDate,
  ]);

  const groupedOrdersByDate = useMemo(() => {
    return (orders || []).reduce<{
      [date: string]: SearchOrders_searchOrders_results[];
    }>((acc, order) => {
      const date = order.createdAt?.slice(0, 10); // group by date (not time)

      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(order);

      return acc;
    }, {});
  }, [orders, onOrderClick]);

  const filteredAvailableCheques = useMemo(() => {
    return availableCheques.filter((cheque) => {
      // Only show for the selected date if any
      if (selectedDate) {
        const chequeDate = DateTime.fromISO(cheque.createdAt!);
        const selected = DateTime.fromISO(selectedDate);
        return (
          chequeDate.hasSame(selected, 'day') &&
          chequeDate.hasSame(selected, 'month') &&
          chequeDate.hasSame(selected, 'year')
        );
      }
      return true;
    });
  }, [availableCheques, selectedDate]);

  // If no order is selected auto select the first one
  useEffect(() => {
    // Includes history is for tablet, in handheld we don't want to auto select and the url in handly does not include "history" in the path
    if (
      orders.length > 0 &&
      !searchParams.get('orderId') &&
      !searchParams.get('chequeId') &&
      location.pathname.includes('/history')
    ) {
      onOrderClick(orders[0].id);
    }
  }, [orders, searchParams, onOrderClick, onChequeClick, location]);

  return (
    <OrdersListContainer>
      <Box marginTop="medium">
        <Box display="flex" gap="xs">
          <Input
            dark
            type="text"
            value={searchParams.get('searchterm') ?? ''}
            placeholder={i18n('Handy.History.Search.Placeholder')}
            onChange={(e) =>
              history.push({
                search: updateSearchParam('searchterm', e.target.value),
              })
            }
          />
          <Input
            type="text"
            value={searchParams.get('searchsum') ?? ''}
            placeholder={i18n('Handy.History.Search.Sum.Placeholder')}
            onChange={(e) =>
              history.push({
                search: updateSearchParam('searchsum', e.target.value),
              })
            }
          />
        </Box>
        <DateInput
          selectedDate={selectedDate ? new Date(selectedDate) : null}
          setSelectedDate={(date) => {
            if (date instanceof Date) {
              history.push({
                search: updateSearchParam('date', date?.toISOString() ?? ''),
              });
            } else {
              // Delete the date param
              const params = new URLSearchParams(location.search);
              params.delete('date');
              history.push({
                search: params.toString(),
              });
            }
          }}
          isDark
        />
      </Box>
      <Box marginTop="small" marginBottom="small">
        <Toggle
          lightText
          fullWidth
          initialValue={storage.filterOrderHistoryByDevice}
          label={i18n('History.OnlyThisDevice')}
          description={i18n('History.OnlyThisDevice.Description')}
          onToggle={() =>
            updateStorage({ filterOrderHistoryByDevice: !storage.filterOrderHistoryByDevice })
          }
        />
      </Box>

      {filteredAvailableCheques.length > 0 && (
        <Box marginBottom="medium">
          <Paragraph level={4} color="var(--colorWhite)" bottom={8}>
            {i18n('History.OpenCheques')}
          </Paragraph>
          {filteredAvailableCheques.map((cheque) => (
            <ChequeRow cheque={cheque} onChequeClick={() => onChequeClick(cheque.id)} />
          ))}
        </Box>
      )}

      {ordersQuery.loading && pageIndex === 0 && <LoadingSkeleton />}

      <>
        {Object.entries(groupedOrdersByDate).map(([date, groupedOrders]) => (
          <Box marginBottom="medium" key={date}>
            <Paragraph level={4} color="var(--colorWhite)" bottom={8}>
              {formatDateLabel(date)}
            </Paragraph>
            {groupedOrders.map((order) => (
              <OrderRow order={order} onOrderClick={() => onOrderClick(order.id)} />
            ))}
          </Box>
        ))}

        {orders.length > 0 && !hideLoadMore && (
          <ShowMoreButton {...loadMoreButton}>
            {ordersQuery.loading ? (
              <Loader size="sm" color="var(--colorBlack)" />
            ) : (
              i18n('Handy.History.LoadMore')
            )}
          </ShowMoreButton>
        )}
      </>
    </OrdersListContainer>
  );
};

export default HistoryOrdersList;
